import { Schema } from "effect";
import { StationId } from "./station-id.ts";
import { schemaParser } from "./schema-compat.ts";
export { StationId } from "./station-id.ts";

export const DEVELOPMENT_IDENTIFIER_KINDS = [
  "station",
  "workspace",
  "blob",
  "tree",
  "commit",
  "worktree",
  "lease",
  "principal",
  "session",
  "change",
  "task",
  "run",
  "attempt",
  "candidate",
  "check-run",
  "review-run",
  "finding",
  "evidence",
  "approval",
  "runtime-namespace",
  "publication-authorization",
  "merge-receipt",
  "rollback-receipt",
  "retention-pin",
  "idempotency"
] as const;

export const DevelopmentIdentifierKind = /* @__PURE__ */ (() => Schema.Literals(DEVELOPMENT_IDENTIFIER_KINDS))();
export type DevelopmentIdentifierKind = typeof DevelopmentIdentifierKind.Type;

const ResourceSlug = /* @__PURE__ */ (() => Schema.String.check(Schema.isPattern(/^[a-z0-9][a-z0-9-]{0,63}$/)))();
const OpaqueIdentifier = /* @__PURE__ */ (() => Schema.String.check(Schema.isPattern(/^[A-Za-z0-9][A-Za-z0-9._:@+-]{0,255}$/)))();

export const WorkspaceId = /* @__PURE__ */ (() => ResourceSlug.pipe(Schema.brand("workspace")))();
export type WorkspaceId = typeof WorkspaceId.Type;
export const BlobId = /* @__PURE__ */ (() => OpaqueIdentifier.pipe(Schema.brand("blob")))();
export type BlobId = typeof BlobId.Type;
export const TreeId = /* @__PURE__ */ (() => OpaqueIdentifier.pipe(Schema.brand("tree")))();
export type TreeId = typeof TreeId.Type;
export const CommitId = /* @__PURE__ */ (() => OpaqueIdentifier.pipe(Schema.brand("commit")))();
export type CommitId = typeof CommitId.Type;
// Chat uses one repository worktree per thread under its recorded chat/ prefix.
export const WorktreeId = /* @__PURE__ */ (() => Schema.String.check(
  Schema.isMaxLength(256), Schema.isPattern(/^(?:chat\/)?[A-Za-z0-9][A-Za-z0-9._:@+-]*$/)
).pipe(Schema.brand("worktree")))();
export type WorktreeId = typeof WorktreeId.Type;
export const WorktreeIdFromRoute = /* @__PURE__ */ (() => Schema.StringFromUriComponent.pipe(Schema.decodeTo(WorktreeId)))();
export const LeaseId = /* @__PURE__ */ (() => OpaqueIdentifier.pipe(Schema.brand("lease")))();
export type LeaseId = typeof LeaseId.Type;
export const PrincipalId = /* @__PURE__ */ (() => OpaqueIdentifier.pipe(Schema.brand("principal")))();
export type PrincipalId = typeof PrincipalId.Type;
export const SessionId = /* @__PURE__ */ (() => OpaqueIdentifier.pipe(Schema.brand("session")))();
export type SessionId = typeof SessionId.Type;
export const ChangeId = /* @__PURE__ */ (() => OpaqueIdentifier.pipe(Schema.brand("change")))();
export type ChangeId = typeof ChangeId.Type;
export const TaskId = /* @__PURE__ */ (() => OpaqueIdentifier.pipe(Schema.brand("task")))();
export type TaskId = typeof TaskId.Type;
export const RunId = /* @__PURE__ */ (() => OpaqueIdentifier.pipe(Schema.brand("run")))();
export type RunId = typeof RunId.Type;
export const AttemptId = /* @__PURE__ */ (() => OpaqueIdentifier.pipe(Schema.brand("attempt")))();
export type AttemptId = typeof AttemptId.Type;
export const CandidateId = /* @__PURE__ */ (() => OpaqueIdentifier.pipe(Schema.brand("candidate")))();
export type CandidateId = typeof CandidateId.Type;
export const CheckRunId = /* @__PURE__ */ (() => OpaqueIdentifier.pipe(Schema.brand("check-run")))();
export type CheckRunId = typeof CheckRunId.Type;
export const ReviewRunId = /* @__PURE__ */ (() => OpaqueIdentifier.pipe(Schema.brand("review-run")))();
export type ReviewRunId = typeof ReviewRunId.Type;
export const FindingId = /* @__PURE__ */ (() => OpaqueIdentifier.pipe(Schema.brand("finding")))();
export type FindingId = typeof FindingId.Type;
export const EvidenceId = /* @__PURE__ */ (() => OpaqueIdentifier.pipe(Schema.brand("evidence")))();
export type EvidenceId = typeof EvidenceId.Type;
export const ApprovalId = /* @__PURE__ */ (() => OpaqueIdentifier.pipe(Schema.brand("approval")))();
export type ApprovalId = typeof ApprovalId.Type;
export const RuntimeNamespaceId = /* @__PURE__ */ (() => OpaqueIdentifier.pipe(Schema.brand("runtime-namespace")))();
export type RuntimeNamespaceId = typeof RuntimeNamespaceId.Type;
export const PublicationAuthorizationId = /* @__PURE__ */ (() => OpaqueIdentifier.pipe(Schema.brand("publication-authorization")))();
export type PublicationAuthorizationId = typeof PublicationAuthorizationId.Type;
export const MergeReceiptId = /* @__PURE__ */ (() => OpaqueIdentifier.pipe(Schema.brand("merge-receipt")))();
export type MergeReceiptId = typeof MergeReceiptId.Type;
export const RollbackReceiptId = /* @__PURE__ */ (() => OpaqueIdentifier.pipe(Schema.brand("rollback-receipt")))();
export type RollbackReceiptId = typeof RollbackReceiptId.Type;
export const RetentionPinId = /* @__PURE__ */ (() => OpaqueIdentifier.pipe(Schema.brand("retention-pin")))();
export type RetentionPinId = typeof RetentionPinId.Type;
export const IdempotencyKey = /* @__PURE__ */ (() => OpaqueIdentifier.pipe(Schema.brand("idempotency")))();
export type IdempotencyKey = typeof IdempotencyKey.Type;

export const DevelopmentIdentifiers = {
  "station": StationId,
  "workspace": WorkspaceId,
  "blob": BlobId,
  "tree": TreeId,
  "commit": CommitId,
  "worktree": WorktreeId,
  "lease": LeaseId,
  "principal": PrincipalId,
  "session": SessionId,
  "change": ChangeId,
  "task": TaskId,
  "run": RunId,
  "attempt": AttemptId,
  "candidate": CandidateId,
  "check-run": CheckRunId,
  "review-run": ReviewRunId,
  "finding": FindingId,
  "evidence": EvidenceId,
  "approval": ApprovalId,
  "runtime-namespace": RuntimeNamespaceId,
  "publication-authorization": PublicationAuthorizationId,
  "merge-receipt": MergeReceiptId,
  "rollback-receipt": RollbackReceiptId,
  "retention-pin": RetentionPinId,
  "idempotency": IdempotencyKey,
} as const;

export type DevelopmentIdentifier<Kind extends DevelopmentIdentifierKind> =
  (typeof DevelopmentIdentifiers)[Kind]["Type"];

export class InvalidDevelopmentIdentifier extends Schema.TaggedError<InvalidDevelopmentIdentifier>()(
  "InvalidDevelopmentIdentifier",
  { kind: DevelopmentIdentifierKind, message: Schema.String, status: Schema.tag(400) }
) {}

/** Keep the public identifier diagnostic consistent across entry boundaries. */
export const developmentIdentifierMessage = (kind: DevelopmentIdentifierKind): string =>
  `${kind} id must be ${kind === "station" || kind === "workspace"
    ? "a non-reserved lowercase resource slug of at most 64 characters"
    : "a non-empty ASCII identifier of at most 256 characters"}.`;

/** Validate an identity at an untrusted boundary and attach its domain brand. */
export function developmentIdentifier<Kind extends DevelopmentIdentifierKind>(
  kind: Kind,
  value: unknown
): DevelopmentIdentifier<Kind> {
  return schemaParser(DevelopmentIdentifiers[kind], () =>
    new InvalidDevelopmentIdentifier({ kind, message: developmentIdentifierMessage(kind) })
  )(value) as DevelopmentIdentifier<Kind>;
}

export const isDevelopmentIdentifier = <Kind extends DevelopmentIdentifierKind>(
  kind: Kind, value: unknown
): value is DevelopmentIdentifier<Kind> => Schema.is(DevelopmentIdentifiers[kind])(value);
